/**
 * Money. Integer centavos, always. No floats touch a córdoba anywhere in this system.
 *
 * C$ 1.00 === 100 centavos.
 *
 * Nicaragua writes numbers US-style: 1,234.56 — comma for thousands, period for decimals.
 */

/**
 * Branded with a literal rather than a `unique symbol`: a symbol brand cannot be named across
 * module boundaries, so any exported type that infers one (a Zod schema holding an FxRate,
 * for instance) fails to compile with TS4023.
 */
export type Centavos = number & { readonly __brand: 'Centavos' };

/** C$ per USD, scaled by 10,000. The 2026 official rate 36.6243 is 366243. */
export type FxRate = number & { readonly __brand: 'FxRate' };

export const FX_SCALE = 10_000;
/** BCN official rate, fixed for all of 2026 (0% slide). Seed value only — the store rate is configurable. */
export const FX_OFICIAL_2026 = 366_243 as FxRate;

/** Largest córdoba amount we accept, as a sanity bound: C$ 100,000,000. */
const MAX_CENTAVOS = 10_000_000_000;

export function centavos(n: number): Centavos {
  if (!Number.isInteger(n)) throw new RangeError(`centavos must be an integer, got ${n}`);
  if (Math.abs(n) > MAX_CENTAVOS) throw new RangeError(`centavos out of range: ${n}`);
  return n as Centavos;
}

export function fxRate(n: number): FxRate {
  if (!Number.isInteger(n) || n <= 0) throw new RangeError(`fx rate must be a positive integer, got ${n}`);
  return n as FxRate;
}

export const ZERO = 0 as Centavos;

/** Round half away from zero. Retail convention, and symmetric for refunds. */
export function roundHalfUp(x: number): number {
  return x < 0 ? -Math.floor(-x + 0.5) : Math.floor(x + 0.5);
}

export function fromCordobas(cordobas: number): Centavos {
  return centavos(roundHalfUp(cordobas * 100));
}

export function toCordobas(c: Centavos): number {
  return c / 100;
}

export function addMoney(...xs: Centavos[]): Centavos {
  return centavos(xs.reduce<number>((a, b) => a + b, 0));
}

export function subMoney(a: Centavos, b: Centavos): Centavos {
  return centavos(a - b);
}

/**
 * Adds amounts that are totals, not one amount: a balance over years of trading, what a product
 * sold since the shop opened. One amount stops at C$ 100,000,000 (`centavos`), a bound for what is
 * typed or scanned, and the balance sheet passed it after a few years of sales: «Contabilidad»,
 * «Análisis» and the reports then failed for every period, for good. A total stops only where
 * numbers stop adding up exactly. Negative terms subtract.
 */
export function sumarTotales(...xs: number[]): Centavos {
  const s = xs.reduce((a, b) => a + b, 0);
  if (!Number.isSafeInteger(s)) throw new RangeError(`total fuera de rango: ${s}`);
  return s as Centavos;
}

export function negMoney(a: Centavos): Centavos {
  return centavos(-a);
}

export function sumMoney(xs: readonly Centavos[]): Centavos {
  return centavos(xs.reduce<number>((a, b) => a + b, 0));
}

/**
 * Money × a scaled integer ratio, computed in BigInt so a large quantity can never
 * silently lose precision through a float. Rounds half away from zero.
 */
export function mulDiv(amount: Centavos, numerator: number, denominator: number): Centavos {
  if (!Number.isInteger(numerator) || !Number.isInteger(denominator)) {
    throw new RangeError('mulDiv requires integer numerator and denominator');
  }
  if (denominator === 0) throw new RangeError('mulDiv: division by zero');
  const n = BigInt(amount) * BigInt(numerator);
  const d = BigInt(denominator);
  const q = n / d;
  const r = n % d;
  const twice = (r < 0n ? -r : r) * 2n;
  const absD = d < 0n ? -d : d;
  if (twice < absD) return centavos(Number(q));
  const away = (n < 0n) !== (d < 0n) ? -1n : 1n;
  return centavos(Number(q + away));
}

/**
 * Money × ratio, rounded UP (toward +∞).
 *
 * For pricing, not for accounting. Half-away-from-zero rounding lands below a requested margin
 * roughly half the time — ask for 25% on a C$16 cost and get 24.99%, which then trips the
 * margin floor that gates what a cashier may do. A price must never quietly undershoot the
 * margin it was asked for; a centavo in the shop's favour is the correct direction to err.
 */
export function mulDivCeil(amount: Centavos, numerator: number, denominator: number): Centavos {
  if (!Number.isInteger(numerator) || !Number.isInteger(denominator)) {
    throw new RangeError('mulDivCeil requires integer numerator and denominator');
  }
  if (denominator === 0) throw new RangeError('mulDivCeil: division by zero');
  const n = BigInt(amount) * BigInt(numerator);
  const d = BigInt(denominator);
  const q = n / d;
  const r = n % d;
  return centavos(Number(r === 0n ? q : (n > 0n) === (d > 0n) ? q + 1n : q));
}

/** Apply a percentage expressed in basis points (1500 bp = 15%). */
export function applyBp(amount: Centavos, bp: number): Centavos {
  return mulDiv(amount, bp, 10_000);
}

/** USD cents -> córdoba centavos at a given store rate. */
export function usdToCordobas(usdCents: Centavos, rate: FxRate): Centavos {
  return mulDiv(usdCents, rate, FX_SCALE);
}

/** Córdoba centavos -> USD cents at a given store rate. */
export function cordobasToUsd(cordobaCentavos: Centavos, rate: FxRate): Centavos {
  return mulDiv(cordobaCentavos, FX_SCALE, rate);
}

/**
 * Round a cash total to a coin step. Default C$0.50 — centavo coins barely circulate
 * in Nicaragua, so a total of C$47.30 is settled as C$47.50.
 * Only ever applied to CASH. Transfers and wallets take the exact amount.
 */
export function roundCash(amount: Centavos, stepCentavos = 50): Centavos {
  if (stepCentavos <= 1) return amount;
  return centavos(roundHalfUp(amount / stepCentavos) * stepCentavos);
}

/** The rounding adjustment itself, which must be posted to its own account so the ledger balances. */
export function cashRoundingDelta(amount: Centavos, stepCentavos = 50): Centavos {
  return subMoney(roundCash(amount, stepCentavos), amount);
}

/**
 * A typed number's commas, read: as thousands where they group by three (`1,234.56`), as the
 * decimal point where a phone in Spanish types it (`12,50`), and null where they could be either
 * (`1,500` with three decimals allowed, one and a half or fifteen hundred) or neither. The result
 * has `.` as its only separator.
 *
 * Every comma used to be dropped, wherever it was: a phone whose keypad has a decimal comma made
 * «12,50» C$ 1,250.00, «1,5» lb fifteen pounds and «5,5» days fifty-five, with nothing said.
 */
export function leerSeparadores(texto: string, maxDecimales: number): string | null {
  if (!texto.includes(',')) return texto;
  const enMiles = /^-?\d{1,3}(,\d{3})+(\.\d*)?$/;
  if (texto.includes('.') || texto.indexOf(',') !== texto.lastIndexOf(',')) {
    return enMiles.test(texto) ? texto.replace(/,/g, '') : null;
  }
  const [entero, decimales] = texto.split(',') as [string, string];
  const comoMiles = /^-?[1-9]\d{0,2}$/.test(entero) && /^\d{3}$/.test(decimales);
  const comoDecimal = /^-?\d*$/.test(entero) && /^\d+$/.test(decimales) && decimales.length <= maxDecimales;
  if (comoMiles && comoDecimal) return null;
  if (comoMiles) return texto.replace(',', '');
  if (comoDecimal) return texto.replace(',', '.');
  return null;
}

/**
 * Parse human input. Tolerates "C$", thousands separators, a decimal comma, spaces, and a bare
 * integer (`leerSeparadores`). Returns null on anything it cannot read — never a silent zero,
 * because a silent zero at a POS becomes a free item.
 */
export function parseMoney(input: string): Centavos | null {
  const cleaned = leerSeparadores(input.trim().replace(/^C\$/i, '').replace(/\s/g, ''), 2);
  if (cleaned === null || cleaned === '' || cleaned === '-') return null;
  if (!/^-?\d*\.?\d*$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  const decimals = cleaned.split('.')[1];
  if (decimals && decimals.length > 2) return null;
  try {
    return fromCordobas(n);
  } catch {
    return null;
  }
}

const groupFmt = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export interface FormatMoneyOptions {
  /** Prefix with "C$ ". Default true. */
  symbol?: boolean;
  /** Show a "+" on positive values, for ledger and variance displays. */
  signed?: boolean;
}

export function formatMoney(c: Centavos, opts: FormatMoneyOptions = {}): string {
  const { symbol = true, signed = false } = opts;
  const sign = c < 0 ? '-' : signed && c > 0 ? '+' : '';
  const body = groupFmt.format(Math.abs(c) / 100);
  return `${sign}${symbol ? 'C$ ' : ''}${body}`;
}

/** Format an FX rate for display: 366243 -> "36.6243". */
export function formatFxRate(rate: FxRate): string {
  return (rate / FX_SCALE).toFixed(4);
}

export function parseFxRate(input: string): FxRate | null {
  const cleaned = leerSeparadores(input.trim(), 4);
  if (cleaned === null || !/^\d*\.?\d*$/.test(cleaned) || cleaned === '' || cleaned === '.') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n <= 0) return null;
  // Too small to be a rate at four decimals, or too large to hold: unreadable, not an error.
  try {
    return fxRate(roundHalfUp(n * FX_SCALE));
  } catch {
    return null;
  }
}

/**
 * Split an amount across weights so the parts sum to EXACTLY the whole.
 *
 * Used to spread freight over the lines of a purchase. Rounding each share independently
 * loses or invents centavos, and a purchase whose allocated freight does not equal the freight
 * paid produces a journal entry that will not balance — which this system refuses to store.
 *
 * Largest-remainder: floor every share, then hand the leftover centavos out one at a time to
 * whichever lines were rounded down hardest. Ties break toward the earlier line, so the same
 * purchase always allocates the same way on replay.
 */
export function repartirProporcional(total: Centavos, pesos: readonly number[]): Centavos[] {
  const n = pesos.length;
  if (n === 0) return [];
  if (total === 0) return pesos.map(() => ZERO);

  const suma = pesos.reduce<number>((a, b) => a + b, 0);

  // Nothing to weigh by (a purchase of all-free lines): split as evenly as the cents allow.
  if (suma <= 0) {
    const base = Math.trunc(total / n);
    const resto = total - base * n;
    return pesos.map((_, i) => centavos(base + (i < Math.abs(resto) ? Math.sign(resto) : 0)));
  }

  const exactos = pesos.map((p) => (total * p) / suma);
  const pisos = exactos.map((x) => Math.trunc(x));
  let sobrante = total - pisos.reduce((a, b) => a + b, 0);

  const orden = exactos
    .map((x, i) => ({ i, frac: x - Math.trunc(x) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);

  const paso = sobrante < 0 ? -1 : 1;
  for (let k = 0; sobrante !== 0 && k < orden.length * 2; k++) {
    const idx = orden[k % orden.length]!.i;
    pisos[idx] = (pisos[idx] as number) + paso;
    sobrante -= paso;
  }

  return pisos.map((v) => centavos(v));
}
